/* eslint-disable react-hooks/exhaustive-deps */
import { Button, ButtonPrimary } from '@/components/button'
import { ArrowBack } from '@/components/icons'
import { PasswordInput } from '@/components/input'
import { ROUTE_DASHBOARD, ROUTE_FORGOT_PASSWORD, ROUTE_LOGIN } from '@/constants/Routes'
import { useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { forgotPassword } from '@/services'
import { ForgotPasswordPayload } from '@/types'
import { createSignUpStep2ValidationSchema } from '@/utils/schema/SignUpStep2ValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

type FormValues = {
  password: string
  confirmPassword: string
}

// Mirrors the server's own wording for a bad code, so a response that carries no message reads
// the same as one that does.
const FALLBACK_RESET_ERROR = 'Incorrect or expired passcode'

export const ResetPassword = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const location = useLocation()

  const { token } = useAppSelector(authSelector)

  const validationSchema = createSignUpStep2ValidationSchema(t)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onTouched',
    resolver: yupResolver(validationSchema),
  })

  const onGoBack = () => navigate(-1)

  const onSubmit = handleSubmit(async (data) => {
    const payload: ForgotPasswordPayload = {
      email: location.state.resetData.email,
      code: location.state.resetData.code,
      password: data.password,
    }
    // This step can and must fail — claiming a password changed when it didn't is worse than an
    // error. It is not an enumeration oracle: the backend answers a wrong code and an unregistered
    // address with the identical "Incorrect or expired passcode". We surface the server's message
    // rather than Axios's ("Request failed with status code 400"), which said nothing useful.
    try {
      const res = await forgotPassword(payload)
      if (res.status === 'SUCCESS') {
        toast.success('Password is updated successfully.')
        navigate(ROUTE_LOGIN, { relative: 'path', replace: true })
      } else {
        toast.error(FALLBACK_RESET_ERROR)
      }
    } catch (error: any) {
      toast.error((error?.response?.data?.message as string) || FALLBACK_RESET_ERROR)
    }
  })

  useEffect(() => {
    if (token) {
      navigate(ROUTE_DASHBOARD, { relative: 'path', replace: true })
    }
  }, [token])

  useEffect(() => {
    if (!location.state) navigate(ROUTE_FORGOT_PASSWORD, { relative: 'path', replace: true })
  }, [location])

  return (
    <div className="w-screen h-screen bg-white flex sm:justify-center sm:items-center">
      <div className="flex flex-col justify-between sm:justify-start pb-14 sm:pb-0 w-full px-4 sm:px-0 sm:w-[390px] gap-y-4">
        <div className="flex relative flex-col sm:flex-row sm:items-center mt-8 sm:mt-0 gap-y-4 pb-0 sm:pb-6">
          <Button onClick={onGoBack} className="hover:opacity-50 flex sm:absolute sm:-left-12">
            <ArrowBack className="text-navy-900" />
          </Button>
          <h1 className="text-2xl xs:text-3xl font-bold text-navy-900">Create New Password</h1>
        </div>

        <div className="flex flex-col gap-y-4 px-3">
          <PasswordInput
            label="Password"
            placeholder="Enter Password"
            labelClassName="!text-navy-900"
            instructs={['Must be at least 6 characters long', 'Must contain one or more numbers']}
            iconClassName="!text-navy-900"
            {...register('password')}
            error={errors.password?.message}
            autoFocus
          />
          <PasswordInput
            label="Confirm Password"
            placeholder="Enter Confirm Password"
            labelClassName="!text-navy-900"
            iconClassName="!text-navy-900"
            {...register('confirmPassword')}
            error={errors.confirmPassword?.message}
            onPaste={(e) => e.preventDefault()}
          />
        </div>
        <div className="flex justify-center items-center px-3">
          <ButtonPrimary title="Submit" className="w-60 !py-[14px]" onClick={onSubmit} />
        </div>
      </div>
    </div>
  )
}
